import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PlayCircle, Music, Users } from "lucide-react";
import { platformClient } from "@/lib/musicApi";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { useHints } from "@/contexts/HintsContext";
import { ordinal } from "@/lib/ordinal";
import { useTranslation } from "react-i18next";
import { BrandMark } from "@/components/common/BrandMark";

const ACCENT = "var(--accent-primary)";
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Tier accent + stable English label (label doubles as an internal comparison key;
// the visible tier name is rendered from i18n at render time).
const TIER_STYLE = {
  founding: { color: "#f5b301", label: "Founding" },
  pioneer: { color: "#c7ccd6", label: "Pioneer" },
  charter: { color: "#cd7f32", label: "Charter" },
  member: { color: ACCENT, label: "Member" },
};

// Tour steps: icons only; titles/bodies come from i18n (onboarding.tour{N}Title/Body).
const TOUR = [{ Icon: PlayCircle }, { Icon: Music }, { Icon: Users }];

const Badge = ({ tier, position }) => {
  const { t } = useTranslation();
  const st = TIER_STYLE[tier] || TIER_STYLE.member;
  const founding = tier === "founding";
  const tierLabel = t(`onboarding.tier${cap(tier)}`);
  const label = st.label !== "Member" ? `${tierLabel} \u00b7 ${ordinal(position)}` : ordinal(position);
  return (
    <div
      data-testid="welcome-badge"
      className="relative inline-flex items-center px-6 py-3 rounded-full text-lg font-bold overflow-hidden"
      style={{ background: `color-mix(in srgb, ${st.color} 12%, transparent)`, color: st.color, border: `2px solid ${st.color}` }}
    >
      {founding && (
        <motion.span
          aria-hidden
          className="absolute inset-0"
          initial={{ x: "-130%" }}
          animate={{ x: "130%" }}
          transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 1.1, ease: "easeInOut" }}
          style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)" }}
        />
      )}
      <span className="relative">{label}</span>
    </div>
  );
};

const Overlay = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[10005] flex items-center justify-center px-6"
    style={{ background: "rgba(8,10,18,0.92)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
  >
    {children}
  </motion.div>
);

/**
 * New-subscriber welcome splash + quick tour (onboarding.md Steps 1, 2, 2.5).
 * Mounted globally. Triggers off a sessionStorage flag set at checkout success
 * plus the subscriber's onboarding scenario. New subscribers get splash -> tour
 * -> (Boss only) the existing Don upgrade popup. Returning subscribers get the
 * welcome-back splash only, no tour, no popup.
 */
export const OnboardingFlow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { platformUser, isSubscriber, isDon, refresh } = usePlatformAuth();
  const { setBusy } = useHints();
  const [mode, setMode] = useState(null); // 'splash' | 'tour' | null
  const [scenario, setScenario] = useState("new");
  const [step, setStep] = useState(0);

  const position = platformUser?.subscriber_position;
  const tier = platformUser?.subscriber_tier || "member";
  const isNew = scenario === "new";

  useEffect(() => {
    if (mode) return;
    let flag = false;
    try { flag = sessionStorage.getItem("samtwo_onboarding") === "1"; } catch { /* noop */ }
    if (!flag || !platformUser) return;
    try { sessionStorage.removeItem("samtwo_onboarding"); } catch { /* noop */ }
    if (!isSubscriber) return;
    setScenario(platformUser.onboarding_scenario || "new");
    setStep(0);
    setMode("splash");
  }, [platformUser, isSubscriber, mode]);

  // Hold back first-time tooltips while the splash/tour is on screen.
  useEffect(() => { setBusy(!!mode); }, [mode, setBusy]);

  const close = useCallback(() => setMode(null), []);

  const finishReturning = useCallback(async () => {
    try { await platformClient.post("/users/me/onboarding/complete", {}); } catch { /* noop */ }
    await refresh();
    close();
  }, [refresh, close]);

  const skip = useCallback(async () => {
    try { await platformClient.post("/users/me/onboarding/skip", {}); } catch { /* noop */ }
    await refresh();
    close();
  }, [refresh, close]);

  const finishTour = useCallback(async () => {
    try { await platformClient.post("/users/me/onboarding/complete", {}); } catch { /* noop */ }
    // Boss subscribers see the existing one-time Don upgrade popup after the tour.
    if (isSubscriber && !isDon) {
      try { sessionStorage.setItem("samtwo_show_don_upgrade", "1"); } catch { /* noop */ }
    }
    close();
    await refresh(); // re-issues platformUser so the Don popup effect reads the flag
    navigate("/music");
  }, [isSubscriber, isDon, refresh, navigate, close]);

  if (!mode) return null;

  return (
    <AnimatePresence>
      {mode === "splash" ? (
        <Overlay key="splash">
          <motion.div
            initial={{ scale: 0.94, y: 16 }}
            animate={{ scale: 1, y: 0 }}
            data-testid="welcome-splash"
            className="w-full max-w-sm text-center flex flex-col items-center"
          >
            <BrandMark testId="onboarding-wordmark" className="mx-auto mb-6" style={{ width: 160 }} />

            <h1 className="text-3xl font-bold mb-6" style={{ color: "#fff" }} data-testid="welcome-headline">
              {scenario === "returning_grace"
                ? t("onboarding.welcomeGrace")
                : scenario === "returning_new_position"
                  ? t("onboarding.welcomeReturningNew", { pos: ordinal(position) })
                  : t("onboarding.welcomeNew", { pos: ordinal(position) })}
            </h1>

            {position != null && <div className="mb-6"><Badge tier={tier} position={position} /></div>}

            {isNew && (
              <>
                {tier !== "member" && (
                  <p className="text-base font-semibold mb-5" data-testid="welcome-tier-line" style={{ color: TIER_STYLE[tier]?.color || ACCENT }}>
                    {t(`onboarding.tierLine${cap(tier)}`)}
                  </p>
                )}
                <p className="text-lg font-bold mb-4" style={{ color: "#fff" }}>{t("onboarding.bossNow")}</p>
                <p className="text-sm mb-8" style={{ color: "rgba(255,255,255,0.75)" }}>{t("onboarding.spotYours")}<br />{t("onboarding.keepIt")}</p>
              </>
            )}

            <button
              onClick={isNew ? () => { setMode("tour"); setStep(0); } : finishReturning}
              data-testid="welcome-cta"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-sm font-semibold"
              style={{ background: ACCENT, color: "#fff" }}
            >
              {t("onboarding.letsGo")}
            </button>

            {isNew && (
              <button onClick={skip} data-testid="welcome-skip" className="mt-5 text-sm" style={{ color: "rgba(255,255,255,0.55)" }}>
                {t("onboarding.skip")}
              </button>
            )}
          </motion.div>
        </Overlay>
      ) : (
        <Overlay key="tour">
          <motion.div
            initial={{ scale: 0.94, y: 16 }}
            animate={{ scale: 1, y: 0 }}
            data-testid="tour-overlay"
            className="w-full max-w-sm text-center flex flex-col items-center"
          >
            <div className="flex items-center gap-2 mb-10" data-testid="tour-dots">
              {TOUR.map((_, i) => (
                <span key={i} className="rounded-full transition-all" style={{ width: i === step ? 22 : 8, height: 8, background: i <= step ? ACCENT : "rgba(255,255,255,0.25)" }} />
              ))}
            </div>

            {(() => { const Icon = TOUR[step].Icon; return (
              <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-8" style={{ background: "color-mix(in srgb, var(--accent-primary) 15%, transparent)", color: ACCENT }}>
                <Icon size={40} />
              </div>
            ); })()}

            <h2 className="text-2xl font-bold mb-3" style={{ color: "#fff" }} data-testid={`tour-title-${step}`}>{t(`onboarding.tour${step}Title`)}</h2>
            <p className="text-sm mb-10 max-w-xs" style={{ color: "rgba(255,255,255,0.75)" }} data-testid={`tour-body-${step}`}>{t(`onboarding.tour${step}Body`)}</p>

            {step < TOUR.length - 1 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                data-testid="tour-next"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-sm font-semibold"
                style={{ background: ACCENT, color: "#fff" }}
              >
                {t("onboarding.next")}
              </button>
            ) : (
              <button
                onClick={finishTour}
                data-testid="tour-start-listening"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-sm font-semibold"
                style={{ background: ACCENT, color: "#fff" }}
              >
                {t("onboarding.startListening")}
              </button>
            )}
          </motion.div>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default OnboardingFlow;
